import React, { useEffect, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Table, { TableColumn } from "../../../components/table/Table";
import TitleBlock from "../../../components/block/TitleBlock";
import HelpHint from "../../../components/help-hint/HelpHint";
import Toggle from "../../../components/form-item/Toggle";
import ToolTip from "../../../components/tooltip/ToolTip";
import SortableList from "../../../components/sortable/SortableList";
import SortableItem from "../../../components/sortable/SortableItem";
import SortableDragHandle from "../../../components/sortable/SortableDragHandle";
import {
  type ProfileReviewCandidate,
  useGetProfileReviewsQuery,
  useReorderProfileReviewsMutation,
  useUpdateProfileReviewMutation,
} from "../../../app/services/creatorProfile";

const COLUMNS: TableColumn[] = [
  { key: "drag", header: "", width: 36, minWidth: 36, resizable: false },
  { key: "show", header: "掲載", width: 84, resizable: false },
  { key: "note", header: "コメント" },
  { key: "reviewer", header: "投稿者", width: 140 },
  { key: "rating", header: "評価", width: 64, resizable: false },
  { key: "status", header: "公開可否", width: 110 },
];

const STATUS_LABEL: Record<string, string> = {
  "1": "実名OK",
  "2": "匿名OK",
  "3": "掲載不可",
};

const EmptyCell = styled.td.attrs({
  className: "bodytext-13 u-text-light-gray u-text-center",
})`
  padding: 16px;
`;

const sortByOrder = (
  reviews: ProfileReviewCandidate[],
): ProfileReviewCandidate[] =>
  [...reviews].sort((a, b) => a.profile_order - b.profile_order);

/**
 * オファー履歴で受け取ったレビューから、公開プロフィールに掲載するものを選別する。
 *
 * - 掲載 ON/OFF は `useUpdateProfileReviewMutation`（PATCH /reviews/:id）。
 * - 並び順は D&D + `useReorderProfileReviewsMutation`（POST /reviews/reorder）。
 *
 * `status='3'`（許可しない）はサーバー側で除外されるため UI に出てこない。
 */
const MySiteReviews: React.FC = () => {
  const { data: reviewsResp } = useGetProfileReviewsQuery();
  const [updateReview] = useUpdateProfileReviewMutation();
  const [reorderReviews] = useReorderProfileReviewsMutation();

  const [localReviews, setLocalReviews] = useState<ProfileReviewCandidate[]>(
    [],
  );
  useEffect(() => {
    setLocalReviews(sortByOrder(reviewsResp?.data?.reviews ?? []));
  }, [reviewsResp]);

  /** サーバーから取得した最新値で localReviews を巻き戻す（更新失敗時のロールバック）。 */
  const rollback = () => {
    setLocalReviews(sortByOrder(reviewsResp?.data?.reviews ?? []));
  };

  const toggleShow = async (id: number, next: boolean) => {
    setLocalReviews((prev) =>
      prev.map((r) => (r.id === id ? { ...r, show_on_profile: next } : r)),
    );
    try {
      await updateReview({ id, show_on_profile: next }).unwrap();
      toast.success(next ? "プロフィールに掲載しました" : "掲載を解除しました");
    } catch {
      toast.error("更新に失敗しました");
      rollback();
    }
  };

  const handleReorder = async (newIds: string[]) => {
    const idMap = new Map(localReviews.map((r) => [String(r.id), r]));
    const reordered = newIds
      .map((id) => idMap.get(id))
      .filter((r): r is ProfileReviewCandidate => r != null);
    setLocalReviews(reordered);
    try {
      await reorderReviews(reordered.map((r) => r.id)).unwrap();
    } catch {
      toast.error("並び替えに失敗しました");
      rollback();
    }
  };

  return (
    <div className="u-col u-gap16">
      {/* 説明は ? アイコンのホバーに畳む（本題の表を上に詰める） */}
      <div className="u-row-between u-items-start u-w100 u-gap8">
        <TitleBlock title="レビュー掲載の選別" />
        <HelpHint description={"オファー履歴で得たレビューから、公開プロフィールに載せるものを選べます。並び替えは行頭のハンドルをドラッグしてください。"} />
      </div>

      <Table columns={COLUMNS} minWidth={640}>
        {localReviews.length === 0 ? (
          <tr>
            <EmptyCell colSpan={COLUMNS.length}>
              掲載できるレビューがありません。
            </EmptyCell>
          </tr>
        ) : (
          <SortableList
            items={localReviews}
            getId={(r) => String(r.id)}
            strategy="vertical"
            axis="y"
            onReorder={handleReorder}
          >
            {localReviews.map((r) => (
              <SortableItem key={r.id} id={String(r.id)}>
                {({ setNodeRef, style, handleProps }) => (
                  <tr ref={setNodeRef} style={style}>
                    <td className="u-text-center">
                      <SortableDragHandle {...handleProps} />
                    </td>
                    <td>
                      <Toggle
                        value={r.show_on_profile}
                        onClick={() => toggleShow(r.id, !r.show_on_profile)}
                      />
                    </td>
                    {/* 見切れ判定のため、インラインの span ではなく td を
                        ラップする（見切れたときだけ全文ツールチップを出す） */}
                    <ToolTip description={r.note} truncatedOnly>
                      <td className="bodytext-13">{r.note}</td>
                    </ToolTip>
                    <td className="bodytext-13">
                      {r.is_anonymous
                        ? `${r.reviewer_name || ""}（匿名で公開）`
                        : r.reviewer_name || "-"}
                    </td>
                    <td className="u-text-center">
                      {r.rating === "LIKE" ? (
                        <span
                          className="material-symbols-rounded u-text-blue"
                          aria-label="高評価"
                        >
                          thumb_up
                        </span>
                      ) : r.rating === "DISLIKE" ? (
                        <span
                          className="material-symbols-rounded u-text-light-gray"
                          aria-label="低評価"
                        >
                          thumb_down
                        </span>
                      ) : (
                        <span className="bodytext-13 u-text-light-gray">
                          —
                        </span>
                      )}
                    </td>
                    <td className="bodytext-13">
                      {STATUS_LABEL[r.status] || r.status}
                    </td>
                  </tr>
                )}
              </SortableItem>
            ))}
          </SortableList>
        )}
      </Table>
    </div>
  );
};

export default MySiteReviews;
